// src/pages/User/index.js - Complete Implementation

import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUsersForAdminThunk,
  approveRejectMultipleUsersThunk,
  blockUnblockMultipleUsersThunk,
  deleteMultipleUsersThunk,
  clearUserError,
  createUserByAdminThunk,
} from "../../store/thunks/userThunks";
import Filter from "../../components/common/Filter";
import Checkbox from "../../components/common/Checkbox";
import { notifyError } from "../../utils/notification";
import UserCardEnhanced from "./components/UserCardEnhanced";
import UserActionModal from "./components/UserActionModal";
import {
  userButtonsConfig,
  bulkUserButtonsConfig,
  userStatusVariants,
  statusMessages,
} from "./constants/userConstants";
import ActionButtons from "../../components/PageUI/ActionButtons";
import Button from "components/common/Button";
import UserModal from "./components/UserModal";

const userFilterStatus = {
  Accepted: "accepted",
  Rejected: "rejected",
  Blocked: "blocked",
  Pending: "pending",
  DeleteRequested: "pending-deletion",
  Unverified: "",
  AdminCreated: "admin-created",
  All: "",
};

const editAction = {
  rejected: "reject",
  blocked: "block",
};

const UsersPage = () => {
  const dispatch = useDispatch();
  const { users, loadingList, loading, error } = useSelector(
    (state) => state.user
  );
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [currentFilter, setCurrentFilter] = useState("Pending");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("");
  const [modalUserIds, setModalUserIds] = useState([]);
  const [modalAllowUpdate, setModalAllowUpdate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    if (error) {
      notifyError(error);
      dispatch(clearUserError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    dispatch(
      getUsersForAdminThunk({
        status: userFilterStatus[currentFilter],
        includeUnverified: currentFilter === "Unverified",
      })
    );
  }, [dispatch, currentFilter]);

  useEffect(() => {
    setSelectAll(selectedUsers.length === users.length && users.length > 0);
  }, [selectedUsers, users]);

  const handleFilterChange = (status) => {
    setCurrentFilter(status);
    setSelectedUsers([]);
    setSelectAll(false);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId)
        : [...prevSelected, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((user) => user._id));
    }
    setSelectAll(!selectAll);
  };

  const openModal = (action, userIds, allowUpdate = false) => {
    setModalAction(action);
    setModalUserIds(userIds);
    setModalAllowUpdate(allowUpdate);
    setIsModalOpen(true);
  };

  const handleBulkAction = (action) => {
    if (action === "delete") {
      if (window.confirm("Are you sure you want to delete these customers?")) {
        dispatch(deleteMultipleUsersThunk(selectedUsers)).then(() => {
          dispatch(
            getUsersForAdminThunk({
              status: userFilterStatus[currentFilter],
              includeUnverified: currentFilter === "Unverified",
            })
          );
          setSelectedUsers([]);
          setSelectAll(false);
        });
      }
    } else if (action === "block" || action === "reject") {
      openModal(action, selectedUsers, action === "reject");
    } else {
      const actionThunk =
        action === "unblock"
          ? blockUnblockMultipleUsersThunk
          : approveRejectMultipleUsersThunk;

      const actionPayload = {
        userIds: selectedUsers,
        approve: action === "accept",
        block: action === "block",
        message: "",
        allowUpdate: action !== "block",
      };

      dispatch(actionThunk(actionPayload)).then(() => {
        dispatch(
          getUsersForAdminThunk({
            status: userFilterStatus[currentFilter],
            includeUnverified: currentFilter === "Unverified",
          })
        );
        setSelectedUsers([]);
        setSelectAll(false);
      });
    }
  };

  const handleSingleAction = (userId, action) => {
    if (action === "delete") {
      if (window.confirm("Are you sure you want to delete this user?")) {
        dispatch(deleteMultipleUsersThunk([userId])).then(() => {
          dispatch(
            getUsersForAdminThunk({
              status: userFilterStatus[currentFilter],
              includeUnverified: currentFilter === "Unverified",
            })
          );
        });
      }
    } else if (action === "block" || action === "reject") {
      openModal(action, [userId], action === "reject");
    } else {
      const actionThunk =
        action === "unblock"
          ? blockUnblockMultipleUsersThunk
          : approveRejectMultipleUsersThunk;

      const actionPayload = {
        userIds: [userId],
        approve: action === "accept",
        block: action === "block",
        message: "",
        allowUpdate: action !== "block",
      };

      dispatch(actionThunk(actionPayload)).then(() => {
        dispatch(
          getUsersForAdminThunk({
            status: userFilterStatus[currentFilter],
            includeUnverified: currentFilter === "Unverified",
          })
        );
      });
    }
  };

  const handleEditAdminMessage = (user) => {
    setEditingUser(user);
    openModal("editMessage", [user._id]);
  };

  const renderBulkActionButtons = () => {
    const config =
      bulkUserButtonsConfig[currentFilter] || bulkUserButtonsConfig.default;

    return (
      <ActionButtons
        buttonsConfig={config}
        onAction={handleBulkAction}
        size="md"
        disabled={!selectedUsers.length}
      />
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalAction("");
    setModalUserIds([]);
    setModalAllowUpdate(false);
    setEditingUser(null);
  };

  const handleModalSubmit = useCallback(
    (message, allowUpdate) => {
      const actionStatus = editAction[editingUser?.status] || modalAction;
      const actionThunk =
        actionStatus === "block" || actionStatus === "unblock"
          ? blockUnblockMultipleUsersThunk
          : approveRejectMultipleUsersThunk;

      const actionPayload = {
        userIds: modalUserIds,
        approve: actionStatus === "accept",
        block: actionStatus === "block",
        message,
        allowUpdate,
      };

      dispatch(actionThunk(actionPayload)).then(() => {
        dispatch(
          getUsersForAdminThunk({
            status: userFilterStatus[currentFilter],
            includeUnverified: currentFilter === "Unverified",
          })
        );
        closeModal();
      });
    },
    [dispatch, modalAction, modalUserIds, currentFilter, editingUser]
  );

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setEditingUser(null);
  };

  const handleCreateUser = async (userData) => {
    const action = await dispatch(createUserByAdminThunk(userData));
    if (createUserByAdminThunk.fulfilled.match(action)) {
      dispatch(
        getUsersForAdminThunk({
          status: userFilterStatus[currentFilter],
          includeUnverified: currentFilter === "Unverified",
        })
      );
      closeCreateModal();
      return null;
    } else {
      return action.payload || "Failed to create user";
    }
  };

  const memoizedUsers = useMemo(() => users, [users]);

  return (
    <div className="container mx-auto p-4 bg-background text-text">
      <h1 className="text-2xl font-bold mb-4">Customers</h1>

      {/* Header with Filters and Actions */}
      <div className="sticky top-0 z-10 bg-background py-2 mb-4">
        <div className="flex flex-col lg:flex-row justify-between items-center mb-2">
          <Filter
            defaultOption="Pending"
            options={[
              "All",
              "Pending",
              "Accepted",
              "Rejected",
              "Blocked",
              "DeleteRequested",
              "Unverified",
              "AdminCreated",
            ]}
            onSelect={handleFilterChange}
            maxTabs={3}
          />
          <div className="flex gap-2 items-center justify-center mt-4 lg:mt-0">
            <div className="text-sm text-gray-600">{`Selected: ${selectedUsers.length}`}</div>
            <Checkbox
              label=""
              name="select-all"
              checked={selectAll}
              onChange={handleSelectAll}
              size="lg"
              className="mx-2 mr-4"
            />
            {renderBulkActionButtons()}
            <div className="mx-2 h-6 border-r border-gray-300"></div>
            <Button variant="primary" onClick={openCreateModal}>
              + Create Customer
            </Button>
          </div>
        </div>
      </div>

      {/* User List */}
      {loadingList ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading customers...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {memoizedUsers.map((user) => (
            <UserCardEnhanced
              key={user._id}
              user={user}
              handleSelectUser={handleSelectUser}
              handleSingleAction={handleSingleAction}
              selected={selectedUsers.includes(user._id)}
              currentFilter={currentFilter}
              onEditAdminMessage={handleEditAdminMessage}
              showAnalytics={true}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loadingList && users.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            No customers found for {currentFilter}
          </p>
          {currentFilter !== "All" && (
            <Button
              variant="secondary"
              onClick={() => handleFilterChange("All")}
              className="mt-4"
            >
              View All Customers
            </Button>
          )}
        </div>
      )}

      {/* Modals */}
      <UserActionModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        action={modalAction}
        userIds={modalUserIds}
        allowUpdate={modalAllowUpdate}
        onSubmit={handleModalSubmit}
        editingUser={editingUser}
      />

      <UserModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        onSubmit={handleCreateUser}
        isLoading={loading}
        error={error}
        mode="create"
      />
    </div>
  );
};

export default memo(UsersPage);
